import { HttpClient, HttpXhrBackend } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { CHANNEL } from '../../../../../constants/channel.constants';
import { IChannelCommunicatorInterface } from '../../../../../interfaces/channel-communicator.interface';
import { ExternalRequest } from '../../../../../models/channel-communicator/external-request.model';
import { ExternalResponse } from '../../../../../models/channel-communicator/external-response.model';
import { ParametersService } from '../../../../parameters/parameters.service';
import { BaseChannelCommunicatorService } from '../../base-channel-communicator.service';

@Injectable()

/**
 * Configuración inicial para la comunicación con superapp
 *
 * *Initial config for comunication with superapp*
 *
 * @type {class}
 * @extends {BaseChannelCommunicatorService}
 * @implements {IChannelCommunicatorInterface}
 */
export class MockAppImplService extends BaseChannelCommunicatorService implements IChannelCommunicatorInterface {
  CHANNEL_ID = CHANNEL.MOCKAPP.CODE;

  private url_mock_sdk: string;

  /**
   * Constructor de la clase
   * *Class Contstructor.*
   *
   * Inicializa los métodos para los modelos
   *
   * *Initialize method for models*
   *
   * @function initService
   */
  constructor(parametersService: ParametersService) {
    super();

    this.initService();

    this.url_mock_sdk = parametersService.getParameter('url_mock_sdk');
  }

  /**
   * Función que obtiene la información de la aplicación
   *
   * *function that obtains app information*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getAppInfo(): Promise<ExternalResponse> {
    return this.channelRequest('/app_properties/');
  }

  /**
   * Función que obtiene la información del usuariopara postLogin
   *
   * *function that gets user info to post login*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getInfoClient(): Promise<ExternalResponse> {
    return this.channelRequest('/get_user_info/');
  }

  /**
   * Función que obtiene la información del dispositivo
   *
   * *function that obtains device information*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getDeviceInfo(): Promise<ExternalResponse> {
    return this.channelRequest('/device_info/');
  }

  /**
   * Función que inicializa las licencias Dsb
   *
   * *function that initializes Dsb licenses*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} license
   * @returns {Promise}
   */
  initDsbLicense(_license: string): Promise<any> {
    return this.channelRequest('/dsb_license/');
  }

  /**
   * Función que valida la seguridad por las reglas de riesgo
   *
   * *Function that validates security by risk rules*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  isSecureByRiskRules(): Promise<any> {
    return this.channelRequest('/risk_rules/');
  }

  /**
   * Función que valida la seguridad del certificado
   *
   * *Function that validates certificate security*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} secureUrl
   * @returns {Promise}
   */
  isSecureCertificate(_secureUrl: string): Promise<any> {
    return this.channelRequest('/secure_certificate/');
  }

  /**
   * Registra la licencia bytte para el escaneo de imágenes
   *
   * *Bytte license registration for images scanning*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseRegistrationUrl
   * @returns {Promise}
   */
  registerBytteLicense(_licenseRegistrationUrl: string): Promise<any> {
    return this.channelRequest('/bytte_license/');
  }

  /**
   * Función que limpia los archivos residuales
   *
   * *Function cleans residual files*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  cleanResidualFiles(): Promise<any> {
    return this.channelRequest('/residual_files/');
  }

  /**
   * Función que verifica y solicita los permisos
   *
   * *Function that verify and request permissions*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  verifyAndRequestPermissions(): Promise<ExternalResponse> {
    return this.channelRequest('/request_permissions/');
  }

  /**
   * Función que envía la información a Dsb
   *
   * *Function that sends data to Dsb*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} userId
   * @returns {Promise}
   */
  sendDataToDSB(_userId: string): Promise<any> {
    return this.channelRequest('/send_data_dsb/');
  }

  /**
   * Función que redirige a la pantalla de login
   *
   * *function that redirect to login screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  redirectToLoginView(): Promise<ExternalResponse> {
    return this.channelRequest('/login_view/');
  }

  /**
   * Función que redirige a la pantalla de lista de agencias
   *
   * *function that redirect to agency lists screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  redirectToAgencyListView(): Promise<ExternalResponse> {
    return this.channelRequest('/agency_list_view/');
  }

  /**
   * Función que redirige a la pantalla para abrir el archivo en pdf
   *
   * *function that redirect to open pdf file*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  openPdf(_url: string): Promise<ExternalResponse> {
    return this.channelRequest('/open_pdf/');
  }

  /**
   * Función que redirige a la pantalla de configuración del dispositivo
   *
   * *Function that redirects to settings device screen*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  goToSettingsDevice(): Promise<ExternalResponse> {
    return this.channelRequest('/settings_device/');
  }

  /**
   * Función que retorna el servicio para escanear la parte de atrás del documento
   *
   * *Function that returns back document scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  backDocumentScan(_licenseAndroid: string, _licenseIos: string, _password: string, _timeout: number): Promise<any> {
    return this.channelRequest('/back_document_scan/');
  }

  /**
   * Función que retorna el servicio para escanear la parte de adelante del documento
   *
   * *Function that returns front document scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  frontDocumentScan(_licenseAndroid: string, _licenseIos: string, _password: string, _timeout: number): Promise<any> {
    return this.channelRequest('/front_document_scan/');
  }

  /**
   * Función que retorna el servicio para escanear las huellas digitales del usuario
   *
   * *Function that returns user's fringer print scan service*
   *
   * @type {function}
   * @type {Promise<any>}
   * @param {string} licenseAndroid
   * @param {string} licenseIos
   * @param {string} password
   * @param {number} timeout
   * @returns {Promise}
   */
  fingerPrintsScan(_fingerPrintSigned: string, _password: string, _timeout: number): Promise<any> {
    return this.channelRequest('/finger_print_scan/');
  }

  /**
   * Función que obtiene token en portal
   *
   * *function that gets token in portal*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getObjectDataPortal(): Promise<ExternalResponse> {
    return this.channelRequest('/get_token_portal/');
  }

  /**
   * Función que trae la data de la vitrina desde portal
   *
   * *Function get data offer from portal*
   *
   * @type {function}
   * @type {Promise<any>}
   * @returns {Promise}
   */
  getFirmlyApprovedPortal(): Promise<ExternalResponse> {
    return this.channelRequest('/get_firmly_approved_portal/');
  }

  /**
   * Creación de la promesa para la comunicación con portalpn
   *
   * *Promise creation for portalpn communication*
   *
   * @param {string} op
   */
  createPromise(op: string): Promise<ExternalResponse> {
    return new Promise((resolve, _reject) => {
      let data: ExternalResponse;

      const callback = (event: any) => {
        try {
          data = JSON.parse(event.data);
        } catch (error) {
          return;
        }
        if (data.origin !== CHANNEL.SUPERAPP.NAME || data.op !== op) { return; }
        resolve(data);

        window.removeEventListener('message', callback);
        document.removeEventListener('message', callback);
      };

      window.addEventListener('message', callback);
      document.addEventListener('message', callback);
    });
  }

  /**
   * Llamada a una función Nativa dependiendo del canal
   *
   * *Calls native function depending on the channel*
   *
   * @param {ExternalRequest} request
   */
  postMessageToChannel(request: ExternalRequest) {
    const getExternalContext = (): any => {
      return new Promise(async (resolve, reject) => {

        const maxTime = 5000;
        const gradient = 100;

        for (let i = gradient; i < maxTime; i += gradient) {
          if (window['ReactNativeWebView']) {
            resolve(window['ReactNativeWebView']);

            return;

          }

          await new Promise(innerResolve => {
            setTimeout(innerResolve, gradient);
          });

        }

        reject('Fatal. External context not found.');
      });
    };

    window.parent.postMessage(JSON.stringify(request), '*');

    getExternalContext().then(context => {
      context.postMessage(JSON.stringify(request));
    }, err => {
      console.error(err + ' Application unavailable.');
    });
  }

  private channelRequest(functionName: string): Promise<ExternalResponse> {
    const case_number = localStorage.getItem('case_number') !== null ? localStorage.getItem('case_number') : '0';

    return this.request(this.url_mock_sdk + functionName + case_number + '.json');
  }

  private request(url: string): Promise<ExternalResponse> {
    const httpClient = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));

    return new Promise<ExternalResponse>((resolve, reject) => {
      httpClient.get(url, { observe: 'response' }).toPromise().then(response => {
        resolve(Object.assign(new ExternalResponse(), response.body));
      }, error => {
        reject(error);
      },
      );
    });
  }
}
